import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { LessonStatus } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

const STATUS_LABEL: Record<LessonStatus, string> = {
  pending: '검토 대기',
  active: '활성',
  rejected: '기각',
  archived: '보관',
};

const STATUS_CLASS: Record<LessonStatus, string> = {
  pending: 'bg-[hsl(30_90%_50%)]/15 text-[hsl(30_90%_50%)]',
  active: 'bg-success/15 text-success',
  rejected: 'bg-destructive/15 text-destructive',
  archived: 'bg-surface-muted text-foreground-muted',
};

export function AdminSkillLessonDetail({ id }: { id: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [guidance, setGuidance] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'skill-lessons', id],
    queryFn: () => api.adminSkillLessonGet(id),
  });

  useEffect(() => {
    if (data) {
      setTitle(data.title);
      // 사람이 한 번도 안 만진 lesson 이고 AI 초안이 있다면 textarea 를 초안으로 prefill.
      // 사람-편집된 guidance 는 그대로 보존 (덮어쓰기 금지).
      if (data.guidance.trim().length === 0 && data.suggestedGuidance) {
        setGuidance(data.suggestedGuidance);
      } else {
        setGuidance(data.guidance);
      }
    }
  }, [data]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'skill-lessons'] });
  };

  const saveMut = useMutation({
    mutationFn: (activate: boolean) =>
      api.adminSkillLessonUpdate(id, {
        title,
        guidance,
        ...(activate ? { activate: true } : {}),
      }),
    onSuccess: (_data, activate) => {
      setFeedback(activate ? '저장 후 활성화했습니다.' : '저장했습니다.');
      setError(null);
      invalidate();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : '저장에 실패했습니다.'),
  });

  const rejectMut = useMutation({
    mutationFn: () => api.adminSkillLessonReject(id),
    onSuccess: () => {
      setFeedback('기각했습니다.');
      setError(null);
      invalidate();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : '실패했습니다.'),
  });

  const archiveMut = useMutation({
    mutationFn: () => api.adminSkillLessonArchive(id),
    onSuccess: () => {
      setFeedback('보관 처리했습니다.');
      setError(null);
      invalidate();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : '실패했습니다.'),
  });

  const suggestMut = useMutation({
    mutationFn: () => api.adminSkillLessonSuggest(id),
    onSuccess: (next) => {
      // AI 가 만든 초안을 textarea 로 즉시 반영 — 사람은 다듬어서 저장만 하면 됨.
      if (next.suggestedGuidance) setGuidance(next.suggestedGuidance);
      setFeedback('AI 초안을 받았습니다. 검토 후 저장하세요.');
      setError(null);
      invalidate();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'AI 초안 생성 실패.'),
  });

  if (isLoading || !data) {
    return (
      <PageShell>
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      </PageShell>
    );
  }

  const inputJson = data.lastInputSample
    ? JSON.stringify(data.lastInputSample, null, 2)
    : '(비어 있음)';

  return (
    <PageShell
      header={
        <PageHeader
          title={data.skillName}
          description={`${data.toolKey} · 실패 ${data.failureCount}회`}
          actions={
            <span
              className={cn(
                'inline-flex items-center px-2 h-6 rounded-sm text-[11px] uppercase tracking-wider',
                STATUS_CLASS[data.status],
              )}
            >
              {STATUS_LABEL[data.status]}
            </span>
          }
        />
      }
    >
      <div className="max-w-4xl">
        {feedback ? (
          <div className="mb-4 rounded-md border border-success/30 bg-success/5 px-3 py-2 text-sm text-success">
            {feedback}
          </div>
        ) : null}
        {error ? (
          <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        ) : null}

        <section className="mb-6 rounded-2xl border border-border bg-surface p-4">
          <h3 className="text-xs uppercase tracking-wider text-foreground-muted mb-2">제목</h3>
          <input
            type="text"
            value={title}
            maxLength={120}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-foreground/20"
          />
        </section>

        <section className="mb-6 rounded-2xl border border-border bg-surface p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs uppercase tracking-wider text-foreground-muted">
              교정 지시문 (guidance)
            </h3>
            <Button
              variant="secondary"
              onClick={() => {
                if (
                  guidance.trim().length > 0 &&
                  !window.confirm('현재 입력 내용을 AI 초안으로 덮어씁니다. 진행할까요?')
                ) {
                  return;
                }
                suggestMut.mutate();
              }}
              disabled={suggestMut.isPending}
            >
              {suggestMut.isPending ? 'AI 작성 중…' : 'AI 초안 받기'}
            </Button>
          </div>
          <p className="text-xs text-foreground-muted mb-2">
            활성화 시 이 Skill 의 system prompt 에 <code>## 경험에서 배운 점</code> 섹션으로
            주입됩니다. 한 줄로 간결하게 작성하세요. 최대 600자.
          </p>
          <textarea
            value={guidance}
            maxLength={600}
            rows={5}
            onChange={(e) => setGuidance(e.target.value)}
            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm font-mono resize-y focus:outline-none focus:ring-1 focus:ring-foreground/20"
            placeholder="예) db.run_query 호출 전 반드시 dataSourceId 를 먼저 결정하세요. 모르면 db.list_tables 를 먼저 호출하세요."
          />
          <div className="mt-1 flex justify-between text-xs text-foreground-muted tabular-nums">
            <span>
              {data.suggestedGuidance
                ? `AI 초안 보유 (${data.suggestedModel ?? '-'})`
                : 'AI 초안 없음'}
            </span>
            <span>{guidance.length} / 600</span>
          </div>
        </section>

        <section className="mb-6 rounded-2xl border border-border bg-surface p-4">
          <h3 className="text-xs uppercase tracking-wider text-foreground-muted mb-2">원본 오류</h3>
          <pre className="whitespace-pre-wrap wrap-break-word bg-background border border-border rounded-md p-3 text-xs font-mono text-foreground-muted max-h-64 overflow-y-auto">
            {data.errorSample}
          </pre>
        </section>

        <section className="mb-6 rounded-2xl border border-border bg-surface p-4">
          <h3 className="text-xs uppercase tracking-wider text-foreground-muted mb-2">
            최근 실패 입력
          </h3>
          <pre className="whitespace-pre-wrap wrap-break-word bg-background border border-border rounded-md p-3 text-xs font-mono text-foreground-muted max-h-64 overflow-y-auto">
            {inputJson}
          </pre>
        </section>

        <div className="flex items-center gap-2">
          <Button
            onClick={() => saveMut.mutate(true)}
            disabled={saveMut.isPending || guidance.trim().length === 0}
          >
            저장 후 활성화
          </Button>
          <Button
            variant="secondary"
            onClick={() => saveMut.mutate(false)}
            disabled={saveMut.isPending}
          >
            저장만
          </Button>
          <Button
            variant="secondary"
            onClick={() => rejectMut.mutate()}
            disabled={rejectMut.isPending}
          >
            기각
          </Button>
          <Button
            variant="secondary"
            onClick={() => archiveMut.mutate()}
            disabled={archiveMut.isPending}
          >
            보관
          </Button>
          <div className="flex-1" />
          <Button variant="secondary" onClick={() => navigate({ to: '/admin/skill-lessons' })}>
            목록으로
          </Button>
        </div>
      </div>
    </PageShell>
  );
}
